import {
  Bold,
  Eraser,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Underline,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn.js';
import { sanitizeHtml } from '../../lib/sanitizeHtml.js';

export interface RichTextEditorProps {
  /** HTML string. */
  value: string;
  onChange: (html: string) => void;
  id?: string;
  ariaLabel?: string;
  placeholder?: string;
}

/** Prose styles for the editor surface AND any rendered (sanitized) output. */
export const RTE_CONTENT_CSS = `
.rte-content { font-size: var(--fs-sm, 14px); line-height: 1.6; color: var(--color-fg); }
.rte-content:focus { outline: none; }
.rte-content p { margin: 0 0 8px; }
.rte-content h2 { font-size: 1.25em; font-weight: 800; margin: 14px 0 6px; }
.rte-content h3 { font-size: 1.1em; font-weight: 700; margin: 12px 0 6px; }
.rte-content ul, .rte-content ol { margin: 0 0 8px; padding-inline-start: 1.4em; }
.rte-content ul { list-style: disc; } .rte-content ol { list-style: decimal; }
.rte-content li { margin: 2px 0; }
.rte-content a { color: var(--color-brand); text-decoration: underline; text-underline-offset: 2px; }
.rte-content blockquote { margin: 8px 0; padding-inline-start: 12px; border-inline-start: 3px solid var(--color-border); color: var(--color-fg-muted); }
.rte-content[data-empty="true"]:before { content: attr(data-placeholder); color: var(--color-fg-muted); pointer-events: none; }
`;

export function RichTextEditor({
  value,
  onChange,
  id,
  ariaLabel,
  placeholder,
}: RichTextEditorProps): JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const lastValue = useRef<string>(' '); // sentinel so the first sync always runs
  const savedRange = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Only push `value` into the DOM when it changed from OUTSIDE (initial load /
  // edit hydration) — never echo our own sanitized emit back, which would jump
  // the caret.
  useEffect(() => {
    const el = ref.current;
    if (el && value !== lastValue.current) {
      lastValue.current = value;
      if (el.innerHTML !== value) el.innerHTML = value || '';
      el.setAttribute('data-empty', el.textContent?.trim() ? 'false' : 'true');
    }
  }, [value]);

  const emit = (): void => {
    const el = ref.current;
    if (!el) return;
    el.setAttribute('data-empty', el.textContent?.trim() ? 'false' : 'true');
    const html = sanitizeHtml(el.innerHTML);
    lastValue.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string): void => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const tool = (
    key: string,
    Icon: typeof Bold,
    run: () => void,
  ): JSX.Element => (
    <button
      type="button"
      className="rte-btn"
      aria-label={t(`richText.${key}`, { defaultValue: key })}
      title={t(`richText.${key}`, { defaultValue: key })}
      // mousedown + preventDefault keeps the editor's selection while clicking.
      onMouseDown={(e) => {
        e.preventDefault();
        run();
      }}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );

  return (
    <div className="rounded-md border border-border bg-bg">
      <style>{RTE_CONTENT_CSS}</style>
      <style>{TOOLBAR_CSS}</style>
      <div className="rte-bar flex flex-wrap items-center gap-0.5 border-b border-border p-1">
        {tool('bold', Bold, () => exec('bold'))}
        {tool('italic', Italic, () => exec('italic'))}
        {tool('underline', Underline, () => exec('underline'))}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        {tool('h2', Heading2, () => exec('formatBlock', '<h2>'))}
        {tool('h3', Heading3, () => exec('formatBlock', '<h3>'))}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        {tool('bulletList', List, () => exec('insertUnorderedList'))}
        {tool('numberList', ListOrdered, () => exec('insertOrderedList'))}
        {tool('link', Link2, () => {
          const sel = window.getSelection();
          savedRange.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
          setLinkUrl('');
          setLinkOpen(true);
        })}
        {tool('clear', Eraser, () => exec('removeFormat'))}
      </div>

      {linkOpen && (
        <div className="flex items-center gap-2 border-b border-border bg-bg-subtle p-2">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://…"
            aria-label={t('richText.linkUrl', { defaultValue: 'Link URL' })}
            className="h-8 flex-1 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              } else if (e.key === 'Escape') {
                setLinkOpen(false);
              }
            }}
          />
          <button type="button" className="rte-apply" onMouseDown={(e) => { e.preventDefault(); applyLink(); }}>
            {t('actions.confirm', { defaultValue: 'Apply' })}
          </button>
        </div>
      )}

      <div
        ref={ref}
        id={id}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        className={cn('rte-content min-h-[140px] px-3 py-2')}
        onInput={emit}
        onBlur={emit}
        onPaste={(e) => {
          // Paste as plain text — strips foreign/malicious markup at the source.
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
      />
    </div>
  );

  function applyLink(): void {
    const el = ref.current;
    const url = linkUrl.trim();
    setLinkOpen(false);
    if (!el || !url) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    const safe = /^(https?:|mailto:)/i.test(url) ? url : `https://${url}`;
    document.execCommand('createLink', false, safe);
    emit();
  }
}

const TOOLBAR_CSS = `
.rte-bar .rte-btn { width: 30px; height: 30px; display: inline-grid; place-items: center; border: 0; background: transparent; color: var(--color-fg-muted); border-radius: 6px; cursor: pointer; }
.rte-bar .rte-btn:hover { background: var(--color-bg-subtle); color: var(--color-fg); }
.rte-apply { height: 32px; padding: 0 12px; border: 0; border-radius: 6px; background: var(--color-brand); color: var(--color-brand-contrast); font-size: 13px; font-weight: 700; cursor: pointer; }
`;
